import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import { CheckMarkCircleIcon } from '@/UI/Icons';
import { AppDialog } from '@/UI/AppDialog/AppDialog';
import { AppXBtn } from '@/UI/AppXBtn/AppXBtn';
import { color } from '@/colors';
import { useCreateSubflowTemplate } from '@/queries/useSubflowTemplates';
import { useGlobalStore } from '@/state/global.state';
import { useFetchMenuItems } from '@/hooks/useFetchMenuItems';
import { SubflowTemplateType } from '@/types/api/subflowTemplate';
import { useFlowView } from '@/components/Recipe/FlowContext';
import { useNodeFiltersStore } from '@/state/nodes/nodes.state';
import { SaveNodeModalBody } from './SaveNodeModalBody';
import { getNodeDefinitionId } from './getChangedParamsDescription';
import { prepareModelNode, resolveSavedModelIconKey } from './saveModelUtils';
import type { NodeId } from 'web';
import type { AiModelData } from '@/types/nodes/model';

interface SaveNodeModalProps {
  nodeId: NodeId;
  onClose: () => void;
  nodeType?: SubflowTemplateType.SavedNode | SubflowTemplateType.SavedModel;
}

export function SaveNodeModal({ nodeId, onClose, nodeType = SubflowTemplateType.SavedNode }: SaveNodeModalProps) {
  const { t } = useTranslation();
  const { mutate, isPending } = useCreateSubflowTemplate();
  const updateSnackbarData = useGlobalStore((s) => s.updateSnackbarData);
  const { fetchMenuItems } = useFetchMenuItems();
  const flowView = useFlowView();
  const menu = useNodeFiltersStore((s) => s.menu);
  const isModel = nodeType === SubflowTemplateType.SavedModel;

  const node = flowView.getNodeNullable(nodeId);
  const initialName = node?.data.name || '';
  // TODO: Auto-populate description from changed params (getChangedParamsDescription) — currently broken
  const initialDescription = '';

  const handleSave = useCallback(
    (saveName: string, saveDescription: string) => {
      if (!node) {
        return;
      }

      const templateNode = isModel ? prepareModelNode(node) : node;

      mutate(
        {
          type: nodeType,
          name: saveName,
          description: saveDescription,
          data: { nodes: [templateNode] },
          icon: isModel ? resolveSavedModelIconKey((node.data as AiModelData).model, menu) : (node.type ?? ''),
          sourceNodeDefinitionId: getNodeDefinitionId(node),
        },
        {
          onSuccess: () => {
            flowView.updateNodeData(nodeId, { name: saveName });
            onClose();
            void fetchMenuItems();
            updateSnackbarData({
              text: t(
                isModel
                  ? I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.SAVE_MODEL_SUCCESS
                  : I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.SAVE_NODE_SUCCESS,
              ),
              isOpen: true,
              icon: <CheckMarkCircleIcon width={20} height={20} />,
            });
          },
          onError: () => {
            updateSnackbarData({
              text: t(
                isModel
                  ? I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.SAVE_MODEL_ERROR
                  : I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.SAVE_NODE_ERROR,
              ),
              isOpen: true,
            });
          },
        },
      );
    },
    [node, flowView, nodeType, nodeId, mutate, onClose, fetchMenuItems, updateSnackbarData, t, isModel, menu],
  );

  const titleKey = isModel
    ? I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.SAVE_MODEL
    : I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.SAVE_NODE;

  return (
    <AppDialog
      open
      onClose={onClose}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: color.Black84_T,
        },
      }}
    >
      <SaveNodeModalBody
        onClose={onClose}
        onSave={handleSave}
        title={t(titleKey)}
        saveButtonLabel={t(titleKey)}
        initialName={initialName}
        initialDescription={initialDescription}
        isSaving={isPending}
      />
      <AppXBtn sx={{ position: 'absolute', top: 12, right: 12 }} onClick={onClose} />
    </AppDialog>
  );
}
